/**
 * 多图栈栅格屏幕切换过渡模式（与 three.js 参考案例 `uMode` 对齐）。
 * - 每图层独立字段 `AnimationLayer.screenImageTransitionMode`，随工程 JSON 持久化。
 * - `localStorage` 键仅作「新建图层」时的默认初始值（见 {@link readStoredScreenImageTransitionModeDefault}）。
 */

/** 无 GPU 过渡，瞬时换图 */
export const SCREEN_IMAGE_TRANSITION_NONE = -1

export const SCREEN_IMAGE_TRANSITION_FADE = 0
export const SCREEN_IMAGE_TRANSITION_SLIDE_LEFT = 1
export const SCREEN_IMAGE_TRANSITION_SLIDE_RIGHT = 2
export const SCREEN_IMAGE_TRANSITION_SLIDE_UP = 3
export const SCREEN_IMAGE_TRANSITION_SLIDE_DOWN = 4
export const SCREEN_IMAGE_TRANSITION_ZOOM = 5
export const SCREEN_IMAGE_TRANSITION_CIRCLE = 6

export type ScreenImageTransitionModeId =
  | typeof SCREEN_IMAGE_TRANSITION_NONE
  | typeof SCREEN_IMAGE_TRANSITION_FADE
  | typeof SCREEN_IMAGE_TRANSITION_SLIDE_LEFT
  | typeof SCREEN_IMAGE_TRANSITION_SLIDE_RIGHT
  | typeof SCREEN_IMAGE_TRANSITION_SLIDE_UP
  | typeof SCREEN_IMAGE_TRANSITION_SLIDE_DOWN
  | typeof SCREEN_IMAGE_TRANSITION_ZOOM
  | typeof SCREEN_IMAGE_TRANSITION_CIRCLE

/** 非滑动类过渡默认时长（秒） */
export const SCREEN_IMAGE_TRANSITION_DURATION_DEFAULT_SEC = 0.85

/** 滑动类过渡默认时长（秒） */
export const SCREEN_IMAGE_TRANSITION_DURATION_SLIDE_SEC = 0.48

/** 时间轴 scrub 跳变超过该阈值（秒）则取消过渡并瞬时对齐 */
export const SCREEN_RASTER_TRANSITION_TIME_JUMP_THRESHOLD_SEC = 0.35

export function screenImageTransitionDurationSec(mode: ScreenImageTransitionModeId): number {
  if (mode === SCREEN_IMAGE_TRANSITION_NONE) return 0
  if (
    mode === SCREEN_IMAGE_TRANSITION_SLIDE_LEFT ||
    mode === SCREEN_IMAGE_TRANSITION_SLIDE_RIGHT ||
    mode === SCREEN_IMAGE_TRANSITION_SLIDE_UP ||
    mode === SCREEN_IMAGE_TRANSITION_SLIDE_DOWN
  ) {
    return SCREEN_IMAGE_TRANSITION_DURATION_SLIDE_SEC
  }
  return SCREEN_IMAGE_TRANSITION_DURATION_DEFAULT_SEC
}

export function isSlideScreenImageTransitionMode(mode: ScreenImageTransitionModeId): boolean {
  return (
    mode === SCREEN_IMAGE_TRANSITION_SLIDE_LEFT ||
    mode === SCREEN_IMAGE_TRANSITION_SLIDE_RIGHT ||
    mode === SCREEN_IMAGE_TRANSITION_SLIDE_UP ||
    mode === SCREEN_IMAGE_TRANSITION_SLIDE_DOWN
  )
}

/** 新建图层时从 `localStorage` 读取的默认过渡模式键（不入图层 JSON 的兜底来源）。 */
export const SCREEN_RASTER_TRANSITION_STORAGE_KEY = 'imockup-editor-screen-raster-transition-mode'

/**
 * 解析任意 JSON / 存储值；非法则返回 `undefined`。
 */
export function parseScreenImageTransitionModeId(raw: unknown): ScreenImageTransitionModeId | undefined {
  if (raw === undefined || raw === null || raw === '') return undefined
  const n = Number(raw)
  if (!Number.isFinite(n)) return undefined
  if (n < SCREEN_IMAGE_TRANSITION_NONE || n > SCREEN_IMAGE_TRANSITION_CIRCLE) return undefined
  return n as ScreenImageTransitionModeId
}

/**
 * 读取「下次添加图层」时的默认过渡模式（会话级）。
 */
export function readStoredScreenImageTransitionModeDefault(): ScreenImageTransitionModeId {
  if (typeof localStorage === 'undefined') return SCREEN_IMAGE_TRANSITION_FADE
  const raw = localStorage.getItem(SCREEN_RASTER_TRANSITION_STORAGE_KEY)
  return parseScreenImageTransitionModeId(raw) ?? SCREEN_IMAGE_TRANSITION_FADE
}

/**
 * 写入「下次添加图层」默认过渡模式；修改某图层过渡时同步调用，便于新图层继承用户最近选择。
 */
export function writeStoredScreenImageTransitionModeDefault(mode: ScreenImageTransitionModeId): void {
  if (typeof localStorage === 'undefined') return
  localStorage.setItem(SCREEN_RASTER_TRANSITION_STORAGE_KEY, String(mode))
}

/**
 * 图层在运行时应使用的过渡模式：显式字段优先，否则为淡入淡出（与旧工程缺省一致）。
 */
export function resolvedLayerScreenImageTransitionMode(layer: {
  screenImageTransitionMode?: ScreenImageTransitionModeId
}): ScreenImageTransitionModeId {
  const ex = layer.screenImageTransitionMode
  if (ex != null) return ex
  return SCREEN_IMAGE_TRANSITION_FADE
}
